import { Artboard } from '@/components/builder/artboard';
import { Field, NumberInput, SelectInput, TextInput } from '@/components/builder/inspector-fields';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useAppTheme, useAssets, useSetAppTheme, type Asset } from '@/lib/queries';
import { SEED_THEME } from '@/lib/seed-theme';
import { useContextStore } from '@/stores/context';
import { TEMPLATES, templateDoc } from '@/templates';
import { stepToExperienceDocument } from '@personalizehq/editor';
import type { Theme, Viewport } from '@personalizehq/schemas/ir';
import { createFileRoute } from '@tanstack/react-router';
import { Moon, Plus, Sun, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_authed/theme')({ component: ThemePage });

// The token editor. Three classes, exactly mirroring `Theme` — and each is why blocks carry no raw
// values at all:
//
//   colors   a light/dark PAIR, always. The editor never offers a single hex, because a single hex
//            has no dark variant and the wire would have to invent one (SPEC §4).
//   fonts    BUNDLED, never system: `src` points at an uploaded font asset. Android ships no Arial
//            and silently falls back to Roboto — which would end line_count as an exact conformance
//            gate forever (SPEC §4.1). `family` alone is a placeholder until an asset is attached.
//   buttons  variants, with a NULLABLE bg (a text-only button is a real variant, not an omission).
//
// The theme is app-level; saving an experience embeds a COPY into that document, because a token is
// a key into a dictionary that must arrive in the same payload. Published bundles are immutable, so
// a theme edit reaches a device by REPUBLISHING. That is correct, not a bug.

const PREVIEW_VIEWPORT: Viewport = { w: 320, h: 560, color_scheme: 'light', font_scale: 1, direction: 'ltr' };

function ThemePage(): React.JSX.Element {
  const appId = useContextStore((s) => s.appId);
  const { data: theme, isLoading } = useAppTheme(appId);
  const { data: assets } = useAssets(appId);
  const save = useSetAppTheme(appId);

  const [draft, setDraft] = useState<Theme | null>(null);
  const [scheme, setScheme] = useState<'light' | 'dark'>('light');

  // An app has NO theme until one is written — the API stores NULL and invents nothing. The seed is
  // the dashboard's, and it becomes real the moment the author saves.
  const current = draft ?? theme ?? SEED_THEME;
  const unsaved = draft !== null || theme == null;

  if (isLoading) return <Skeleton className="h-96 w-full rounded-xl" />;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Theme</h1>
          <p className="text-sm text-muted-foreground">
            The tokens every block resolves against. Blocks never carry raw hex — that is why this page exists.
          </p>
        </div>
        <Button
          disabled={!unsaved || save.isPending}
          onClick={() =>
            save.mutate(current, {
              onSuccess: () => {
                setDraft(null);
                toast.success('Theme saved. Republish a campaign for devices to see it.');
              },
              onError: (error: Error) => toast.error(error.message),
            })
          }
        >
          {save.isPending ? 'Saving…' : 'Save theme'}
        </Button>
      </div>

      {theme == null ? (
        <p className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-sm text-amber-600 dark:text-amber-400">
          This app has no theme yet. What you see is the starter set — save it to make it real.
        </p>
      ) : null}

      <div className="grid grid-cols-[1fr_340px] gap-6">
        <div className="space-y-6">
          <Colors theme={current} onChange={setDraft} />
          <Fonts theme={current} assets={assets ?? []} onChange={setDraft} />
          <Buttons theme={current} onChange={setDraft} />
        </div>

        {/* The preview is the PRODUCTION renderer over a real template — the same code path the
            device runs, re-laid-out on every token edit. */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Preview</span>
            <Button variant="outline" size="sm" onClick={() => setScheme(scheme === 'light' ? 'dark' : 'light')}>
              {scheme === 'light' ? <Sun className="size-4" /> : <Moon className="size-4" />}
              {scheme}
            </Button>
          </div>
          <div className="flex justify-center rounded-xl bg-zinc-900 p-4">
            <ThemePreview theme={current} scheme={scheme} assets={assets ?? []} />
          </div>
        </div>
      </div>
    </div>
  );
}

function ThemePreview({
  theme,
  scheme,
  assets,
}: {
  theme: Theme;
  scheme: 'light' | 'dark';
  assets: Asset[];
}): React.JSX.Element {
  const doc = useMemo(() => {
    const template = TEMPLATES[0]!; // the modal — the densest sample of all three token classes
    const authoring = templateDoc(template, theme);
    return stepToExperienceDocument(authoring, authoring.steps[0]!.id);
  }, [theme]);

  const assetMap = useMemo(() => Object.fromEntries(assets.map((a) => [a.id, a.url])), [assets]);
  const viewport = useMemo(() => ({ ...PREVIEW_VIEWPORT, color_scheme: scheme }), [scheme]);

  return <Artboard doc={doc} viewport={viewport} selection={null} onSelect={() => {}} assets={assetMap} />;
}

// ---------------------------------------------------------------------------- sections

/** Tokens can be added and deleted, not RENAMED: a rename silently breaks every block that
 *  references the old key, across every draft in the app. Delete + re-add makes the breakage
 *  visible (the inspector flags "not a token in this theme") instead of silent. */
function TokenSection({
  title,
  description,
  onAdd,
  children,
}: {
  title: string;
  description: string;
  onAdd: (name: string) => void;
  children: React.ReactNode;
}): React.JSX.Element {
  const [name, setName] = useState('');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {children}
        <form
          className="flex gap-2 border-t pt-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim() === '') return;
            onAdd(name.trim());
            setName('');
          }}
        >
          <Input
            className="h-8 max-w-[220px]"
            placeholder="new token name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Button type="submit" variant="outline" size="sm" disabled={name.trim() === ''}>
            <Plus className="size-4" />
            Add
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function DeleteToken({ onClick }: { onClick: () => void }): React.JSX.Element {
  return (
    <button
      type="button"
      aria-label="Delete token"
      className="text-muted-foreground hover:text-destructive"
      onClick={onClick}
    >
      <Trash2 className="size-3.5" />
    </button>
  );
}

function Colors({ theme, onChange }: { theme: Theme; onChange: (t: Theme) => void }): React.JSX.Element {
  const set = (colors: Theme['colors']): void => onChange({ ...theme, colors });

  return (
    <TokenSection
      title="Colors"
      description="Every color is a light/dark pair. There is no single-value color, anywhere."
      onAdd={(name) => set({ ...theme.colors, [name]: { light: '#000000', dark: '#ffffff' } })}
    >
      {Object.entries(theme.colors).map(([name, pair]) => (
        <div key={name} className="flex items-center gap-3">
          <span className="w-40 shrink-0 truncate font-mono text-xs">{name}</span>
          {(['light', 'dark'] as const).map((mode) => (
            <label key={mode} className="flex items-center gap-1.5 text-xs text-muted-foreground">
              {mode}
              <input
                type="color"
                aria-label={`${name} ${mode}`}
                className="size-7 cursor-pointer rounded border bg-transparent"
                value={pair[mode]}
                onChange={(e) => set({ ...theme.colors, [name]: { ...pair, [mode]: e.target.value } })}
              />
            </label>
          ))}
          <span className="ml-auto">
            <DeleteToken
              onClick={() => {
                const next = { ...theme.colors };
                delete next[name];
                set(next);
              }}
            />
          </span>
        </div>
      ))}
    </TokenSection>
  );
}

function Fonts({
  theme,
  assets,
  onChange,
}: {
  theme: Theme;
  assets: Asset[];
  onChange: (t: Theme) => void;
}): React.JSX.Element {
  const set = (fonts: Theme['fonts']): void => onChange({ ...theme, fonts });
  // A FONT asset, not any asset — a PNG in a `src` registers as a font face and fails silently.
  const fontAssets = assets.filter(
    (a) => a.content_type.includes('font') || /\.(ttf|otf|woff2?)$/i.test(a.r2_key)
  );

  return (
    <TokenSection
      title="Fonts"
      description="Bundled fonts only — a system family renders differently on every OS and moves every wrap point."
      onAdd={(name) =>
        set({
          ...theme.fonts,
          [name]: { family: 'Inter', size: 16, weight: 400, line_height: 1.5, max_scale: 1.6 },
        })
      }
    >
      {Object.entries(theme.fonts).map(([name, font]) => (
        <div key={name} className="space-y-2 rounded-lg border p-3">
          <div className="flex items-center justify-between">
            <span className="font-mono text-xs">{name}</span>
            <DeleteToken
              onClick={() => {
                const next = { ...theme.fonts };
                delete next[name];
                set(next);
              }}
            />
          </div>
          <div className="grid grid-cols-2 gap-x-6">
            <Field label="family">
              <TextInput
                aria-label={`${name} family`}
                value={font.family}
                onChange={(family) => set({ ...theme.fonts, [name]: { ...font, family } })}
              />
            </Field>
            <Field label="font file">
              <SelectInput
                aria-label={`${name} src`}
                value={font.src ?? ''}
                options={['', ...fontAssets.map((a) => a.id)]}
                onChange={(src) => set({ ...theme.fonts, [name]: { ...font, src: src || undefined } })}
              />
            </Field>
            <Field label="size">
              <NumberInput
                aria-label={`${name} size`}
                min={1}
                value={font.size}
                onChange={(size) => set({ ...theme.fonts, [name]: { ...font, size: size ?? 16 } })}
              />
            </Field>
            <Field label="weight">
              <NumberInput
                aria-label={`${name} weight`}
                min={100}
                max={900}
                step={100}
                value={font.weight}
                onChange={(weight) => set({ ...theme.fonts, [name]: { ...font, weight: weight ?? 400 } })}
              />
            </Field>
            <Field label="line height">
              <NumberInput
                aria-label={`${name} line height`}
                min={0.1}
                max={4}
                step={0.05}
                value={font.line_height}
                onChange={(lh) => set({ ...theme.fonts, [name]: { ...font, line_height: lh ?? 1.5 } })}
              />
            </Field>
            {/* The clamp on Dynamic Type: a 2.0× system scale against max_scale 1.6 MUST clamp.
                This is the field the builder's font-scale toggles exist to let you feel. */}
            <Field label="max scale">
              <NumberInput
                aria-label={`${name} max scale`}
                min={1}
                max={4}
                step={0.1}
                value={font.max_scale}
                onChange={(ms) => set({ ...theme.fonts, [name]: { ...font, max_scale: ms ?? 1.6 } })}
              />
            </Field>
          </div>
        </div>
      ))}
      {fontAssets.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No font files uploaded. Add one on the Assets page, then attach it here.
        </p>
      ) : null}
    </TokenSection>
  );
}

const NO_BG = '__none__';

function Buttons({ theme, onChange }: { theme: Theme; onChange: (t: Theme) => void }): React.JSX.Element {
  const set = (buttons: Theme['buttons']): void => onChange({ ...theme, buttons });
  const colors = Object.keys(theme.colors);
  const first = colors[0] ?? '';

  return (
    <TokenSection
      title="Button variants"
      description="A background, a foreground, a radius and padding. The background may be none — that is a text button."
      onAdd={(name) =>
        set({
          ...theme.buttons,
          [name]: { bg: first, fg: first, radius: 10, padding: { top: 12, end: 20, bottom: 12, start: 20 } },
        })
      }
    >
      {Object.entries(theme.buttons).map(([name, variant]) => (
        <div key={name} className="space-y-2 rounded-lg border p-3">
          <div className="flex items-center justify-between">
            <span className="font-mono text-xs">{name}</span>
            <DeleteToken
              onClick={() => {
                const next = { ...theme.buttons };
                delete next[name];
                set(next);
              }}
            />
          </div>
          <div className="grid grid-cols-2 gap-x-6">
            <Field label="background">
              <SelectInput
                aria-label={`${name} bg`}
                value={variant.bg ?? NO_BG}
                options={[NO_BG, ...colors]}
                onChange={(bg) => set({ ...theme.buttons, [name]: { ...variant, bg: bg === NO_BG ? null : bg } })}
              />
            </Field>
            <Field label="foreground">
              <SelectInput
                aria-label={`${name} fg`}
                value={variant.fg}
                options={colors}
                onChange={(fg) => set({ ...theme.buttons, [name]: { ...variant, fg } })}
              />
            </Field>
            <Field label="radius">
              <NumberInput
                aria-label={`${name} radius`}
                min={0}
                value={variant.radius}
                onChange={(radius) => set({ ...theme.buttons, [name]: { ...variant, radius: radius ?? 0 } })}
              />
            </Field>
            <Field label="padding">
              <span className="grid grid-cols-4 gap-1">
                {(['top', 'end', 'bottom', 'start'] as const).map((side) => (
                  <NumberInput
                    key={side}
                    className="w-full"
                    aria-label={`${name} padding ${side}`}
                    min={0}
                    value={variant.padding[side]}
                    onChange={(value) =>
                      set({
                        ...theme.buttons,
                        [name]: { ...variant, padding: { ...variant.padding, [side]: value ?? 0 } },
                      })
                    }
                  />
                ))}
              </span>
            </Field>
          </div>
        </div>
      ))}
    </TokenSection>
  );
}
